import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
  Button,
  Modal,
} from "react-bootstrap";
import api from "../../services/api";
import "./Dashboard.css";

const jenisPelatihanLabel = {
  PPH01: "Penyelia Halal UMK Self Declare",
  PPH02: "Penyelia Halal UMK Reguler",
  PPH03: "Penyelia Halal Reguler",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PesertaDaftarPeserta = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [jenisList, setJenisList] = useState([]);
  const [angkatanList, setAngkatanList] = useState([]);
  const [filterJenis, setFilterJenis] = useState("");
  const [filterAngkatan, setFilterAngkatan] = useState("");
  const [searchText, setSearchText] = useState("");

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  // Modal konfirmasi hasil
  const [showModal, setShowModal] = useState(false);
  const [targetRow, setTargetRow] = useState(null);
  const [targetHasil, setTargetHasil] = useState(""); // "LULUS" | "TIDAK LULUS"

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterJenis, filterAngkatan]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/pendaftaran-penyelia/filter-options");
      if (res.data.success) {
        setJenisList(res.data.jenis_pelatihan || []);
        setAngkatanList(res.data.angkatan || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filterJenis) params.jenis_pelatihan = filterJenis;
      if (filterAngkatan) params.angkatan = filterAngkatan;
      const res = await api.get("/pendaftaran-penyelia/daftar-peserta", { params });
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError("Gagal memuat data.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data dari server.");
    } finally {
      setLoading(false);
    }
  }, [filterJenis, filterAngkatan]);

  const filteredData = data.filter((row) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      row.nama?.toLowerCase().includes(term) ||
      row.email?.toLowerCase().includes(term) ||
      row.no_hp?.toLowerCase().includes(term) ||
      row.nik?.toLowerCase().includes(term) ||
      row.no_register?.toLowerCase().includes(term)
    );
  });

  const openModal = (row, hasil) => {
    setTargetRow(row);
    setTargetHasil(hasil);
    setActionError("");
    setShowModal(true);
  };

  const handleSetHasil = async () => {
    if (!targetRow) return;
    try {
      setActionLoading(true);
      setActionError("");
      await api.put("/pendaftaran-penyelia/hasil-peserta", {
        email: targetRow.email,
        hasil: targetHasil,
      });
      setShowModal(false);
      setTargetRow(null);
      fetchData();
    } catch (err) {
      setActionError(err.response?.data?.message || "Gagal mengubah status.");
    } finally {
      setActionLoading(false);
    }
  };

  const isLulus = targetHasil === "LULUS";

  return (
    <Container fluid>
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-users me-2"></i>
          Daftar Peserta Pelatihan
        </h2>
        <p className="text-muted mb-0">
          Peserta dengan status{" "}
          <Badge bg="primary">PESERTA PELATIHAN</Badge>
        </p>
      </div>

      {/* Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => { setFilterJenis(e.target.value); setFilterAngkatan(""); }}
              >
                <option value="">— Semua —</option>
                {jenisList.map((j) => (
                  <option key={j} value={j}>{jenisPelatihanLabel[j] || j}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Angkatan</Form.Label>
              <Form.Select
                value={filterAngkatan}
                onChange={(e) => setFilterAngkatan(e.target.value)}
              >
                <option value="">— Semua —</option>
                {angkatanList.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold mb-1">Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, email, NIK, no. register..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={2} className="text-end">
              <span className="text-muted small">{filteredData.length} data</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>No. Register</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>No. HP</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Angkatan</th>
                  <th>Tgl. Daftar</th>
                  <th className="text-center">Hasil</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => (
                    <tr key={row.email}>
                      <td className="ps-3">{idx + 1}</td>
                      <td>{row.no_register || "-"}</td>
                      <td className="fw-semibold">{row.nama || "-"}</td>
                      <td>{row.email}</td>
                      <td>{row.no_hp || "-"}</td>
                      <td>{row.nik || "-"}</td>
                      <td>{jenisPelatihanLabel[row.jenis_pelatihan] || row.jenis_pelatihan || "-"}</td>
                      <td>{row.angkatan || "-"}</td>
                      <td>{formatDate(row.tgl_daftar)}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="success"
                          className="me-1"
                          onClick={() => openModal(row, "LULUS")}
                        >
                          <i className="fas fa-check me-1"></i>Lulus
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => openModal(row, "TIDAK LULUS")}
                        >
                          <i className="fas fa-times me-1"></i>Tidak Lulus
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Konfirmasi */}
      <Modal show={showModal} onHide={() => !actionLoading && setShowModal(false)} centered>
        <Modal.Header
          closeButton
          className={isLulus ? "bg-success text-white" : "bg-danger text-white"}
        >
          <Modal.Title>
            <i className={`fas ${isLulus ? "fa-check-circle" : "fa-times-circle"} me-2`}></i>
            Konfirmasi Hasil Pelatihan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p>
            Tandai peserta berikut sebagai{" "}
            <strong>
              <Badge bg={isLulus ? "success" : "danger"}>{targetHasil}</Badge>
            </strong>?
          </p>
          {targetRow && (
            <table className="table table-sm table-borderless">
              <tbody>
                <tr><td className="text-muted">Nama</td><td className="fw-semibold">{targetRow.nama}</td></tr>
                <tr><td className="text-muted">Email</td><td>{targetRow.email}</td></tr>
                <tr><td className="text-muted">Jenis Pelatihan</td><td>{jenisPelatihanLabel[targetRow.jenis_pelatihan] || targetRow.jenis_pelatihan}</td></tr>
                <tr><td className="text-muted">Angkatan</td><td>{targetRow.angkatan}</td></tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={actionLoading}>
            Batal
          </Button>
          <Button
            variant={isLulus ? "success" : "danger"}
            onClick={handleSetHasil}
            disabled={actionLoading}
          >
            {actionLoading
              ? <Spinner animation="border" size="sm" className="me-1" />
              : <i className={`fas ${isLulus ? "fa-check" : "fa-times"} me-1`}></i>}
            Ya, {targetHasil}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PesertaDaftarPeserta;
